// =====================================================================================
// useEditModeHistory - Undo/redo stack for decoration edit mode
// =====================================================================================
// Command pattern: each UndoableAction stores data to reverse/replay itself
// in both React state (stagedChanges, localDecorationIds) and Phaser visuals.

import { useRef, useState, useCallback } from 'react';
import type { DecorationChange } from '@shared/schemas/decorations.schema.js';
import type { DecorationConfig } from '../game/systems/decorations/types.js';

/**
 * DecorationSystem.addDecoration() returns "decoration_X" but stores with key "X".
 * Extract the real stored ID for Phaser lookups.
 */
function toStoredId(returnedId: string): string {
  const match = returnedId.match(/^decoration_(\d+)$/);
  return match ? match[1] : returnedId;
}

// ── Types ───────────────────────────────────────────────────────────────────────

export interface SpriteState {
  x: number;
  y: number;
  depth: number;
}

export interface UndoableAction {
  type: 'add' | 'remove' | 'update';
  objectId: number;
  decorationId: string;

  /** Config needed to recreate sprite in Phaser (for add redo / remove undo) */
  decorationConfig?: DecorationConfig;

  // ── 'add' data ──
  /** The staged {type:'add', object} entry */
  addedEntry?: DecorationChange;

  // ── 'remove' data ──
  /** Whether the removed decoration was locally-added (in localDecorationIds) */
  wasLocal?: boolean;
  /** If wasLocal: the original 'add' entry that was removed from stagedChanges */
  removedAddEntry?: DecorationChange;
  /** Sprite position at time of removal (needed to restore moved decorations) */
  removedSpriteState?: SpriteState;

  // ── 'update' data ──
  /** Previous stagedChanges update entry for this objectId (null if first update) */
  prevUpdateEntry?: DecorationChange | null;
  /** The update entry created by this action */
  nextUpdateEntry?: DecorationChange;
  /** Sprite state before this update */
  prevSpriteState?: SpriteState;
  /** Sprite state after this update */
  nextSpriteState?: SpriteState;
}

// ── Hook ────────────────────────────────────────────────────────────────────────

/**
 * Manages undo/redo stacks for decoration edit mode.
 * Uses refs for stack data and a version counter to trigger re-renders.
 */
export function useEditModeHistory(
  getGameScene: () => any,
  setStagedChanges: React.Dispatch<React.SetStateAction<DecorationChange[]>>,
  localDecorationIds: React.MutableRefObject<Set<number>>
) {
  const undoStackRef = useRef<UndoableAction[]>([]);
  const redoStackRef = useRef<UndoableAction[]>([]);
  const [historyVersion, setHistoryVersion] = useState(0);

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  // ── Stack operations ────────────────────────────────────────────────────────

  const pushAction = useCallback((action: UndoableAction) => {
    undoStackRef.current = [...undoStackRef.current, action];
    redoStackRef.current = [];
    setHistoryVersion(v => v + 1);
  }, []);

  const resetHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setHistoryVersion(v => v + 1);
  }, []);

  // ── Undo execution ─────────────────────────────────────────────────────────

  const executeUndo = useCallback((action: UndoableAction) => {
    const scene = getGameScene();
    const ds = scene?.systems?.decorations;

    switch (action.type) {
      case 'add': {
        // Remove sprite from Phaser
        ds?.removeDecoration(action.decorationId);
        // Remove the 'add' entry from staged changes
        setStagedChanges(prev => prev.filter(c =>
          !(c.type === 'add' && c.object?.id === action.objectId)
        ));
        // Remove from local IDs
        localDecorationIds.current.delete(action.objectId);
        break;
      }

      case 'remove': {
        // Recreate sprite in Phaser
        if (ds && action.decorationConfig) {
          const rawId = ds.addDecoration({ ...action.decorationConfig });
          if (rawId) {
            const storedId = toStoredId(rawId);
            action.decorationId = storedId;
            // Restore to position at time of removal (handles moved decorations)
            if (action.removedSpriteState) {
              ds.updateDecorationPosition(storedId, action.removedSpriteState.x, action.removedSpriteState.y);
              const dec = ds.getDecoration(storedId);
              if (dec) dec.sprite.setDepth(action.removedSpriteState.depth);
            }
          }
        }

        if (action.wasLocal) {
          // Restore the original 'add' entry
          if (action.removedAddEntry) {
            setStagedChanges(prev => [...prev, action.removedAddEntry!]);
          }
          localDecorationIds.current.add(action.objectId);
        } else {
          // Remove the 'remove' entry from staged
          setStagedChanges(prev => prev.filter(c =>
            !(c.type === 'remove' && c.objectId === action.objectId)
          ));
        }
        break;
      }

      case 'update': {
        // Move sprite back to previous position
        if (ds && action.prevSpriteState) {
          ds.updateDecorationPosition(action.decorationId, action.prevSpriteState.x, action.prevSpriteState.y);
          const dec = ds.getDecoration(action.decorationId);
          if (dec) dec.sprite.setDepth(action.prevSpriteState.depth);
        }

        // Restore previous staged entry
        if (action.prevUpdateEntry === null) {
          // First update for this object: remove the entry entirely
          setStagedChanges(prev => prev.filter(c =>
            !(c.type === 'update' && c.objectId === action.objectId)
          ));
        } else if (action.prevUpdateEntry) {
          // Replace with previous entry
          setStagedChanges(prev => prev.map(c =>
            (c.type === 'update' && c.objectId === action.objectId)
              ? action.prevUpdateEntry!
              : c
          ));
        }
        break;
      }
    }
  }, [getGameScene, setStagedChanges, localDecorationIds]);

  // ── Redo execution ──────────────────────────────────────────────────────────

  const executeRedo = useCallback((action: UndoableAction) => {
    const scene = getGameScene();
    const ds = scene?.systems?.decorations;

    switch (action.type) {
      case 'add': {
        // Recreate sprite in Phaser
        if (ds && action.decorationConfig) {
          const rawId = ds.addDecoration({ ...action.decorationConfig });
          if (rawId) action.decorationId = toStoredId(rawId);
        }
        // Re-add the staged entry
        if (action.addedEntry) {
          setStagedChanges(prev => [...prev, action.addedEntry!]);
        }
        localDecorationIds.current.add(action.objectId);
        break;
      }

      case 'remove': {
        // Remove from Phaser
        ds?.removeDecoration(action.decorationId);

        if (action.wasLocal) {
          // Remove the 'add' entry from staged
          setStagedChanges(prev => prev.filter(c =>
            !(c.type === 'add' && c.object?.id === action.objectId)
          ));
          localDecorationIds.current.delete(action.objectId);
        } else {
          // Re-add the 'remove' entry
          setStagedChanges(prev => [...prev, { type: 'remove' as const, objectId: action.objectId }]);
        }
        break;
      }

      case 'update': {
        // Move sprite to new position
        if (ds && action.nextSpriteState) {
          ds.updateDecorationPosition(action.decorationId, action.nextSpriteState.x, action.nextSpriteState.y);
          const dec = ds.getDecoration(action.decorationId);
          if (dec) dec.sprite.setDepth(action.nextSpriteState.depth);
        }

        // Add/replace staged entry
        if (action.nextUpdateEntry) {
          setStagedChanges(prev => {
            const idx = prev.findIndex(c => c.type === 'update' && c.objectId === action.objectId);
            if (idx !== -1) {
              const updated = [...prev];
              updated[idx] = action.nextUpdateEntry!;
              return updated;
            }
            return [...prev, action.nextUpdateEntry!];
          });
        }
        break;
      }
    }
  }, [getGameScene, setStagedChanges, localDecorationIds]);

  // ── Public undo/redo ────────────────────────────────────────────────────────

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return;

    const action = stack[stack.length - 1];
    undoStackRef.current = stack.slice(0, -1);
    redoStackRef.current = [...redoStackRef.current, action];

    executeUndo(action);
    setHistoryVersion(v => v + 1);
  }, [executeUndo]);

  const redo = useCallback(() => {
    const stack = redoStackRef.current;
    if (stack.length === 0) return;

    const action = stack[stack.length - 1];
    redoStackRef.current = stack.slice(0, -1);
    undoStackRef.current = [...undoStackRef.current, action];

    executeRedo(action);
    setHistoryVersion(v => v + 1);
  }, [executeRedo]);

  return {
    pushAction,
    undo,
    redo,
    canUndo,
    canRedo,
    resetHistory,
    /** Exposed for context memoization (changes when stacks change) */
    historyVersion,
  };
}
